<template>
  <div class="cb">
    <!-- Toggle de modo -->
    <div class="cb-mode">
      <button
        type="button"
        class="cb-mode__btn"
        :class="{ 'cb-mode__btn--active': isVisualMode }"
        @click="isVisualMode = true">
        <fa-icon icon="filter" size="sm" /> Visual
      </button>
      <button
        type="button"
        class="cb-mode__btn"
        :class="{ 'cb-mode__btn--active': !isVisualMode }"
        @click="isVisualMode = false">
        <fa-icon icon="code" size="sm" /> SQL
      </button>
    </div>

    <!-- Modo Visual -->
    <div v-if="isVisualMode" class="cb-visual">
      <div v-if="!entidade" class="cb-empty">
        <fa-icon icon="info-circle" class="cb-empty__icon" />
        <span>Selecione o tipo de tag acima para configurar as condições.</span>
      </div>

      <div v-else-if="loadingFields" class="cb-loading">
        <div class="spinner-border spinner-border-sm" role="status"></div>
        <span>Carregando campos...</span>
      </div>

      <div v-else>
        <!-- Grupos -->
        <div
          v-for="(grupo, gIndex) in conditions.grupos"
          :key="gIndex"
          class="cb-group">

          <!-- Conector AND entre grupos -->
          <div v-if="gIndex > 0" class="cb-connector">
            <span class="cb-connector__line"></span>
            <span class="cb-connector__label">E</span>
            <span class="cb-connector__line"></span>
          </div>

          <div class="cb-group__card">
            <!-- Header do grupo -->
            <div class="cb-group__header">
              <div class="cb-group__title">
                <span class="cb-group__number">{{ gIndex + 1 }}</span>
                <span>Atende</span>
                <select
                  v-model="grupo.logica"
                  class="cb-group__logic"
                  @change="emitUpdate">
                  <option value="AND">todas</option>
                  <option value="OR">qualquer uma</option>
                </select>
                <span>das condições</span>
              </div>
              <button
                type="button"
                class="cb-btn-icon cb-btn-icon--danger"
                @click="removeGroup(gIndex)"
                :disabled="conditions.grupos.length <= 1"
                title="Remover grupo">
                <fa-icon icon="trash-alt" size="sm" />
              </button>
            </div>

            <!-- Condições -->
            <div class="cb-conditions">
              <div
                v-for="(cond, cIndex) in grupo.condicoes"
                :key="cIndex"
                class="cb-row">

                <!-- Prefixo -->
                <div class="cb-row__prefix">
                  <span v-if="cIndex === 0" class="cb-row__prefix-text">Se</span>
                  <span v-else class="cb-row__prefix-text cb-row__prefix-text--logic">
                    {{ grupo.logica === 'AND' ? 'E' : 'OU' }}
                  </span>
                </div>

                <!-- Campo -->
                <div class="cb-row__field">
                  <select
                    v-model="cond.campo"
                    class="cb-select"
                    @change="onFieldChange(cond)">
                    <option value="">Campo...</option>
                    <option
                      v-for="field in fields"
                      :key="field.campo"
                      :value="field.campo">
                      {{ field.label }}
                    </option>
                  </select>
                </div>

                <!-- Operador -->
                <div class="cb-row__operator">
                  <select
                    v-model="cond.operador"
                    class="cb-select"
                    @change="onOperatorChange(cond)"
                    :disabled="!cond.campo">
                    <option value="">Operador...</option>
                    <option
                      v-for="op in getOperatorsForField(cond.campo)"
                      :key="op.value"
                      :value="op.value">
                      {{ op.label }}
                    </option>
                  </select>
                </div>

                <!-- Condição de compra: alvo (produto/departamento) + período -->
                <div
                  class="cb-row__value"
                  v-if="isCompraField(cond.campo) && cond.operador && cond.valor && cond.valor.periodo">
                  <div class="cb-compra">
                    <multiselect
                      v-if="getFieldDef(cond.campo).alvo === 'produto'"
                      v-model="cond.valor.produtos"
                      :options="getOptionsValuesForField('produto')"
                      :custom-label="(val) => getOptionLabel('produto', val)"
                      :multiple="true"
                      :close-on-select="false"
                      :internal-search="false"
                      :loading="loadingOptions.produto"
                      placeholder="Digite para buscar o produto..."
                      @search-change="buscarProdutos"
                      @input="emitUpdate"
                      :select-label="''"
                      :selected-label="''"
                      :deselect-label="''"
                      class="cb-multiselect">
                      <template #noResult>Nenhum produto encontrado.</template>
                      <template #noOptions>Digite ao menos 2 caracteres para buscar.</template>
                    </multiselect>

                    <multiselect
                      v-else
                      v-model="cond.valor.dimensoes"
                      :options="getOptionsValuesForField('dimensao')"
                      :custom-label="(val) => getOptionLabel('dimensao', val)"
                      :multiple="true"
                      :close-on-select="false"
                      :loading="loadingOptions.dimensao"
                      placeholder="Selecione o departamento..."
                      @open="loadFieldOptions(cond.campo)"
                      @input="emitUpdate"
                      :select-label="''"
                      :selected-label="''"
                      :deselect-label="''"
                      class="cb-multiselect">
                      <template #noResult>Nenhum departamento encontrado.</template>
                      <template #noOptions>Nenhum departamento cadastrado.</template>
                    </multiselect>

                    <div class="cb-compra__periodo">
                      <select
                        v-model="cond.valor.periodo.tipo"
                        class="cb-select cb-select--periodo"
                        @change="onPeriodoCompraChange(cond)">
                        <option
                          v-for="periodo in periodosCompra"
                          :key="periodo.value"
                          :value="periodo.value">
                          {{ periodo.label }}
                        </option>
                      </select>
                      <input
                        v-if="periodoCompraPrecisaQuantidade(cond)"
                        type="number"
                        min="1"
                        v-model.number="cond.valor.periodo.quantidade"
                        class="cb-input cb-input--quantidade"
                        placeholder="N"
                        @input="emitUpdate" />
                    </div>
                  </div>
                </div>

                <!-- Valor -->
                <div
                  class="cb-row__value"
                  v-else-if="cond.operador && !isNoValueOperator(cond.operador)">
                  <div class="cb-value-group">
                    <!-- Tipo valor -->
                    <select
                      v-model="cond.tipo_valor"
                      class="cb-select cb-select--tipo"
                      @change="onTipoValorChange(cond)"
                      title="Tipo de valor">
                      <option value="static">Fixo</option>
                      <option value="parametro">Param.</option>
                      <option v-if="getFieldDef(cond.campo).tipo === 'date'" value="named_param">Relativo</option>
                    </select>

                    <!-- Valor fixo -->
                    <template v-if="cond.tipo_valor === 'static'">
                      <select
                        v-if="getFieldDef(cond.campo).tipo === 'boolean'"
                        v-model="cond.valor"
                        class="cb-select cb-select--value"
                        @change="emitUpdate">
                        <option :value="1">Sim</option>
                        <option :value="0">Nao</option>
                      </select>

                      <!-- "Contém" procura por parte do texto: é digitado, mesmo em
                           campo que também oferece lista (Classe, Categoria, Município). -->
                      <input
                        v-else-if="['contem', 'nao_contem'].includes(cond.operador)"
                        type="text"
                        v-model="cond.valor"
                        class="cb-input"
                        placeholder="Parte do texto"
                        @input="emitUpdate" />

                      <input
                        v-else-if="getFieldDef(cond.campo).tipo === 'number' && cond.operador !== 'entre'"
                        type="number"
                        v-model.number="cond.valor"
                        class="cb-input"
                        placeholder="Valor"
                        @input="emitUpdate" />

                      <div v-else-if="getFieldDef(cond.campo).tipo === 'number' && cond.operador === 'entre'" class="cb-between">
                        <input
                          type="number"
                          v-model.number="cond.valor[0]"
                          class="cb-input"
                          placeholder="De"
                          @input="emitUpdate" />
                        <span class="cb-between__sep">e</span>
                        <input
                          type="number"
                          v-model.number="cond.valor[1]"
                          class="cb-input"
                          placeholder="Ate"
                          @input="emitUpdate" />
                      </div>

                      <multiselect
                        v-else-if="getFieldDef(cond.campo).tipo === 'select' && ['em', 'nao_em'].includes(cond.operador)"
                        v-model="cond.valor"
                        :options="getOptionsValuesForField(cond.campo)"
                        :custom-label="(val) => getOptionLabel(cond.campo, val)"
                        :multiple="true"
                        :close-on-select="false"
                        :loading="loadingOptions[cond.campo]"
                        placeholder="Selecione..."
                        @open="loadFieldOptions(cond.campo)"
                        @input="emitUpdate"
                        :select-label="''"
                        :selected-label="''"
                        :deselect-label="''"
                        class="cb-multiselect">
                        <template #noResult
                          >Nenhum resultado encontrado. Tente ajustar a
                          busca.</template
                        >
                        <template #noOptions>A lista está vazia.</template>
                      </multiselect>

                      <multiselect
                        v-else-if="getFieldDef(cond.campo).tipo === 'select'"
                        v-model="cond.valor"
                        :options="getOptionsValuesForField(cond.campo)"
                        :custom-label="(val) => getOptionLabel(cond.campo, val)"
                        :loading="loadingOptions[cond.campo]"
                        placeholder="Selecione..."
                        @open="loadFieldOptions(cond.campo)"
                        @input="emitUpdate"
                        :select-label="''"
                        :selected-label="''"
                        :deselect-label="''"
                        class="cb-multiselect">
                        <template #noResult
                          >Nenhum resultado encontrado. Tente ajustar a
                          busca.</template
                        >
                        <template #noOptions>A lista está vazia.</template>
                      </multiselect>

                      <input
                        v-else-if="getFieldDef(cond.campo).tipo === 'date' && cond.operador !== 'entre'"
                        type="date"
                        v-model="cond.valor"
                        class="cb-input"
                        @input="emitUpdate" />

                      <div v-else-if="getFieldDef(cond.campo).tipo === 'date' && cond.operador === 'entre'" class="cb-between">
                        <input
                          type="date"
                          v-model="cond.valor[0]"
                          class="cb-input"
                          @input="emitUpdate" />
                        <span class="cb-between__sep">e</span>
                        <input
                          type="date"
                          v-model="cond.valor[1]"
                          class="cb-input"
                          @input="emitUpdate" />
                      </div>

                      <input
                        v-else
                        type="text"
                        v-model="cond.valor"
                        class="cb-input"
                        placeholder="Valor"
                        @input="emitUpdate" />
                    </template>

                    <!-- Valor relativo (named_param) -->
                    <template v-if="cond.tipo_valor === 'named_param'">
                      <select
                        v-if="cond.operador !== 'entre'"
                        v-model="cond.valor"
                        class="cb-select cb-select--value"
                        @change="emitUpdate">
                        <option value="">Selecione...</option>
                        <option
                          v-for="param in namedDateParams"
                          :key="param.value"
                          :value="param.value">
                          {{ param.label }}
                        </option>
                      </select>
                      <div v-else class="cb-between">
                        <select
                          v-model="cond.valor[0]"
                          class="cb-select cb-select--value"
                          @change="emitUpdate">
                          <option value="">De...</option>
                          <option
                            v-for="param in namedDateParams"
                            :key="param.value"
                            :value="param.value">
                            {{ param.label }}
                          </option>
                        </select>
                        <span class="cb-between__sep">e</span>
                        <select
                          v-model="cond.valor[1]"
                          class="cb-select cb-select--value"
                          @change="emitUpdate">
                          <option value="">Ate...</option>
                          <option
                            v-for="param in namedDateParams"
                            :key="param.value"
                            :value="param.value">
                            {{ param.label }}
                          </option>
                        </select>
                      </div>
                    </template>

                    <!-- Valor dinamico (parametro) -->
                    <template v-if="cond.tipo_valor === 'parametro'">
                      <input
                        type="text"
                        v-model="cond.parametro.label"
                        class="cb-input"
                        placeholder="Label do parametro"
                        @input="emitUpdate" />
                      <span
                        class="cb-badge cb-badge--info"
                        :title="cond.operador === 'entre'
                          ? 'Faixa solicitada ao usuário no momento do uso (De e Até em um único parâmetro)'
                          : 'Valor solicitado ao usuário no momento do uso'">
                        <fa-icon icon="info-circle" size="sm" />
                        {{ cond.operador === 'entre' ? 'Dinâmico (faixa)' : 'Dinâmico' }}
                      </span>
                    </template>
                  </div>
                </div>

                <!-- Indicador operador sem valor -->
                <div v-else-if="isNoValueOperator(cond.operador)" class="cb-row__value">
                  <span class="cb-badge cb-badge--muted">Sem valor</span>
                </div>

                <!-- Remover -->
                <div class="cb-row__remove">
                  <button
                    type="button"
                    class="cb-btn-icon cb-btn-icon--danger"
                    @click="removeCondition(gIndex, cIndex)"
                    :disabled="grupo.condicoes.length <= 1"
                    title="Remover">
                    <fa-icon icon="times" size="sm" />
                  </button>
                </div>
              </div>
            </div>

            <!-- Adicionar condicao -->
            <button
              type="button"
              class="cb-add-condition"
              @click="addCondition(gIndex)">
              <fa-icon icon="plus" size="sm" /> Adicionar condicao
            </button>
          </div>
        </div>

        <!-- Adicionar grupo -->
        <button
          type="button"
          class="cb-add-group"
          @click="addGroup()">
          <fa-icon icon="plus" size="sm" /> Adicionar grupo de condicoes
        </button>

        <!-- Preview SQL -->
        <div class="cb-preview" v-if="sqlPreview || previewErro">
          <div class="cb-preview__header">
            <span class="cb-preview__title">SQL gerado</span>
            <button
              type="button"
              class="cb-btn-icon"
              @click="refreshPreview"
              title="Atualizar preview">
              <fa-icon icon="sync-alt" size="sm" />
            </button>
          </div>
          <div v-if="previewErro" class="cb-preview__erro">
            <fa-icon icon="exclamation-triangle" size="sm" /> {{ previewErro }}
          </div>
          <pre v-else class="cb-preview__code">{{ sqlPreview }}</pre>
        </div>
      </div>
    </div>

    <!-- Modo SQL -->
    <div v-else class="cb-sql">
      <codemirror v-model="rawSql" :options="codeOptions" @input="onRawSqlChange"></codemirror>
    </div>
  </div>
</template>

<script>
import ncformCommon from '@ncform/ncform-common';
import { get, post } from '@/helpers/apiRequest';
import { codemirror } from 'vue-codemirror';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';

import 'codemirror/lib/codemirror.css';
import 'codemirror/theme/monokai.css';
import 'codemirror/mode/sql/sql.js';
import 'codemirror/addon/display/autorefresh.js';
import 'codemirror/addon/selection/active-line.js';

const OPERATOR_LABELS = {
  igual: 'Igual a',
  diferente: 'Diferente de',
  maior: 'Maior que',
  menor: 'Menor que',
  maior_igual: 'Maior ou igual a',
  menor_igual: 'Menor ou igual a',
  em: 'Está em',
  nao_em: 'Não está em',
  entre: 'Entre',
  contem: 'Contém',
  nao_contem: 'Não contém',
  comprou: 'Comprou',
  nao_comprou: 'Não comprou',
  nulo: 'É nulo',
  nao_nulo: 'Não é nulo',
};

// Período inicial das condições de compra: sem janela, o cadastro fica explícito.
function novoPeriodoCompra() {
  return { tipo: 'qualquer', quantidade: 3 };
}

function newCondition() {
  return {
    campo: '',
    operador: '',
    valor: null,
    tipo_valor: 'static',
    parametro: { nome: '', label: '', tipo: 'text', multiplo: false },
  };
}

function newGroup() {
  return {
    logica: 'AND',
    condicoes: [newCondition()],
  };
}

export default {
  name: 'ConditionBuilder',
  mixins: [ncformCommon.mixins.vue.controlMixin],
  components: { codemirror, Multiselect },

  data() {
    return {
      isVisualMode: true,
      fields: [],
      namedDateParams: [],
      periodosCompra: [],
      fieldOptionsCache: {},
      loadingOptions: {},
      loadingFields: false,
      conditions: { grupos: [newGroup()] },
      rawSql: '',
      sqlPreview: '',
      previewErro: '',
      previewTimeout: null,
      buscaProdutoTimeout: null,
      codeOptions: {
        tabSize: 4,
        styleActiveLine: true,
        lineNumbers: true,
        line: true,
        mode: 'text/x-sql',
        autoRefresh: true,
        lineWrapping: true,
        theme: 'monokai',
        showCursorWhenSelecting: true,
      },
    };
  },

  computed: {
    entidade() {
      return this.formData ? this.formData.entidade : null;
    },
  },

  watch: {
    modelVal: {
      handler(newVal) {
        if (!newVal) return;
        if (typeof newVal === 'string') {
          try {
            const parsed = JSON.parse(newVal);
            if (parsed && parsed.grupos) {
              this.conditions = this.normalizarCondicoes(parsed);
              this.isVisualMode = true;
            } else {
              this.rawSql = newVal;
              this.isVisualMode = false;
            }
          } catch {
            this.rawSql = newVal;
            this.isVisualMode = false;
          }
        } else if (typeof newVal === 'object' && newVal.grupos) {
          this.conditions = this.normalizarCondicoes(newVal);
          this.isVisualMode = true;
        }
      },
      immediate: true,
    },

    entidade: {
      handler(newEntidade) {
        if (newEntidade) {
          this.loadFields(newEntidade);
        }
      },
      immediate: true,
    },

    isVisualMode() {
      this.emitUpdate();
    },
  },

  methods: {
    async loadFields(entidade) {
      this.loadingFields = true;
      try {
        const data = await get(`/admin/tag-filtro/campos/${entidade}`);
        this.fields = data.campos || [];
        this.namedDateParams = data.named_date_params || [];
        this.periodosCompra = data.periodos_compra || [];
      } catch (e) {
        console.error('Erro ao carregar campos:', e);
        this.fields = [];
      } finally {
        this.loadingFields = false;
      }
    },

    async loadFieldOptions(campo) {
      const fieldDef = this.getFieldDef(campo);
      if (!fieldDef || !fieldDef.opcoes_url) return;

      // As condições de compra guardam as opções pela origem (produto, dimensao),
      // porque o mesmo alvo pode aparecer em várias condições da tag.
      const chave = this.isCompraField(campo) ? fieldDef.opcoes_url : campo;

      if (this.fieldOptionsCache[chave] || this.loadingOptions[chave]) return;

      this.$set(this.loadingOptions, chave, true);
      try {
        const opcoes = await get(`/admin/tag-filtro/opcoes/${this.entidade}/${fieldDef.opcoes_url}`);
        this.$set(this.fieldOptionsCache, chave, opcoes || []);
      } catch (e) {
        console.error('Erro ao carregar opcoes:', e);
        this.$set(this.fieldOptionsCache, chave, []);
      } finally {
        this.$set(this.loadingOptions, chave, false);
      }
    },

    /**
     * Busca produtos pelo que o usuário digita. São milhares no cadastro, então a lista
     * nunca vem inteira: o que já veio fica acumulado no cache para os itens
     * selecionados continuarem com rótulo.
     */
    buscarProdutos(termo) {
      if (this.buscaProdutoTimeout) clearTimeout(this.buscaProdutoTimeout);
      if (!termo || termo.trim().length < 2) return;

      this.buscaProdutoTimeout = setTimeout(async () => {
        this.$set(this.loadingOptions, 'produto', true);
        try {
          const opcoes = await get(
            `/admin/tag-filtro/opcoes/${this.entidade}/produto?termo=${encodeURIComponent(termo.trim())}`
          );
          const atuais = this.fieldOptionsCache.produto || [];
          const valoresAtuais = atuais.map(o => o.value);
          const novas = (opcoes || []).filter(o => !valoresAtuais.includes(o.value));
          this.$set(this.fieldOptionsCache, 'produto', atuais.concat(novas));
        } catch (e) {
          console.error('Erro ao buscar produtos:', e);
        } finally {
          this.$set(this.loadingOptions, 'produto', false);
        }
      }, 400);
    },

    getFieldDef(campo) {
      return this.fields.find(f => f.campo === campo) || { tipo: 'text', operadores: [] };
    },

    getOperatorsForField(campo) {
      const fieldDef = this.getFieldDef(campo);
      return (fieldDef.operadores || []).map(op => ({
        value: op,
        label: OPERATOR_LABELS[op] || op,
      }));
    },

    getOptionsForField(campo) {
      return this.fieldOptionsCache[campo] || [];
    },

    getOptionsValuesForField(campo) {
      return (this.fieldOptionsCache[campo] || []).map(o => o.value);
    },

    getOptionLabel(campo, val) {
      const options = this.fieldOptionsCache[campo] || [];
      const found = options.find(o => o.value === val);
      return found ? found.label : val;
    },

    isNoValueOperator(operador) {
      return ['nulo', 'nao_nulo'].includes(operador);
    },

    /**
     * Campo de condição de compra ("comprou produto X", "não comprou do departamento Y").
     * O valor é composto (alvo + período), por isso tem bloco próprio na tela e não
     * usa o seletor de fixo/parâmetro.
     */
    isCompraField(campo) {
      return this.getFieldDef(campo).tipo === 'compra';
    },

    novoValorCompra() {
      return { produtos: [], dimensoes: [], periodo: novoPeriodoCompra() };
    },

    /**
     * Garante a estrutura do valor de uma condição de compra (tags antigas ou salvas
     * pela API podem vir sem alguma parte).
     */
    garantirValorCompra(cond) {
      const valor = cond.valor && typeof cond.valor === 'object' && !Array.isArray(cond.valor)
        ? cond.valor
        : {};

      cond.valor = {
        produtos: Array.isArray(valor.produtos) ? valor.produtos : [],
        dimensoes: Array.isArray(valor.dimensoes) ? valor.dimensoes : [],
        periodo: valor.periodo && typeof valor.periodo === 'object'
          ? { tipo: valor.periodo.tipo || 'qualquer', quantidade: valor.periodo.quantidade || 3 }
          : novoPeriodoCompra(),
      };

      return cond.valor;
    },

    periodoCompraPrecisaQuantidade(cond) {
      const tipo = cond.valor && cond.valor.periodo ? cond.valor.periodo.tipo : null;
      const periodo = this.periodosCompra.find(p => p.value === tipo);
      return !!(periodo && periodo.quantidade);
    },

    onPeriodoCompraChange(cond) {
      if (this.periodoCompraPrecisaQuantidade(cond) && !cond.valor.periodo.quantidade) {
        cond.valor.periodo.quantidade = 3;
      }
      this.emitUpdate();
    },

    /**
     * Normaliza as condições carregadas: completa a estrutura das condições de compra e
     * o parâmetro de faixa, para o que já está salvo abrir do jeito certo na tela.
     */
    normalizarCondicoes(condicoes) {
      (condicoes.grupos || []).forEach(grupo => {
        (grupo.condicoes || []).forEach(cond => {
          if (!cond.parametro) {
            cond.parametro = { nome: '', label: '', tipo: 'text', multiplo: false };
          }

          if (this.isCompraField(cond.campo)) {
            cond.tipo_valor = 'static';
            this.garantirValorCompra(cond);
            return;
          }

          if (cond.tipo_valor === 'parametro') {
            this.sincronizarParametro(cond);
          }
        });
      });

      return condicoes;
    },

    /**
     * Ajusta o parâmetro ao operador. No "Entre" o usuário informa UM parâmetro só, que
     * carrega os dois lados da faixa (De e Até) — dois parâmetros separados obrigariam
     * a preencher a mesma coisa duas vezes no momento do uso.
     */
    sincronizarParametro(cond) {
      const fieldDef = this.getFieldDef(cond.campo);
      const faixa = cond.operador === 'entre';

      // "Contém" pede um texto digitado, e não a lista de valores do campo.
      const ehTexto = ['contem', 'nao_contem'].includes(cond.operador);

      cond.parametro = {
        nome: (cond.parametro && cond.parametro.nome) || cond.campo.replace('.', '_').toLowerCase(),
        label: (cond.parametro && cond.parametro.label) || fieldDef.label || cond.campo,
        tipo: faixa ? 'range' : ehTexto ? 'text' : fieldDef.tipo || 'text',
        multiplo: ['em', 'nao_em'].includes(cond.operador),
        faixa,
        tipo_valor_faixa: faixa ? fieldDef.tipo || 'text' : undefined,
      };
    },

    onFieldChange(cond) {
      cond.operador = '';
      cond.valor = null;
      cond.tipo_valor = 'static';
      cond.parametro = { nome: '', label: '', tipo: 'text', multiplo: false };

      const fieldDef = this.getFieldDef(cond.campo);

      if (this.isCompraField(cond.campo)) {
        cond.valor = this.novoValorCompra();
        // Departamento vem de lista curta e já pode carregar; produto é buscado.
        if (fieldDef.alvo === 'dimensao') {
          this.loadFieldOptions(cond.campo);
        }
      } else if (fieldDef.tipo === 'select' && fieldDef.opcoes_url) {
        this.loadFieldOptions(cond.campo);
      }

      this.emitUpdate();
    },

    onOperatorChange(cond) {
      const fieldDef = this.getFieldDef(cond.campo);

      // Compra: trocar comprou/não comprou não mexe no alvo nem no período.
      if (this.isCompraField(cond.campo)) {
        this.garantirValorCompra(cond);
        this.emitUpdate();
        return;
      }

      // Parâmetro: o valor continua sendo pedido no uso — só o formato do parâmetro
      // muda (faixa no "Entre", lista no "Está em").
      if (cond.tipo_valor === 'parametro') {
        cond.valor = null;
        this.sincronizarParametro(cond);
        this.emitUpdate();
        return;
      }

      if (cond.operador === 'entre') {
        cond.valor = fieldDef.tipo === 'date' && cond.tipo_valor === 'named_param'
          ? ['', '']
          : [null, null];
      } else if (['em', 'nao_em'].includes(cond.operador)) {
        cond.valor = [];
      } else if (this.isNoValueOperator(cond.operador)) {
        cond.valor = null;
      } else {
        cond.valor = fieldDef.tipo === 'boolean' ? 1 : null;
      }
      this.emitUpdate();
    },

    onTipoValorChange(cond) {
      if (cond.tipo_valor === 'named_param') {
        cond.valor = cond.operador === 'entre' ? ['', ''] : '';
      } else if (cond.tipo_valor === 'parametro') {
        cond.parametro = null;
        this.sincronizarParametro(cond);
        cond.valor = null;
      } else {
        cond.valor = cond.operador === 'entre' ? [null, null] : null;
      }
      this.emitUpdate();
    },

    addGroup() {
      this.conditions.grupos.push(newGroup());
      this.emitUpdate();
    },

    removeGroup(gIndex) {
      if (this.conditions.grupos.length > 1) {
        this.conditions.grupos.splice(gIndex, 1);
        this.emitUpdate();
      }
    },

    addCondition(gIndex) {
      this.conditions.grupos[gIndex].condicoes.push(newCondition());
      this.emitUpdate();
    },

    removeCondition(gIndex, cIndex) {
      const grupo = this.conditions.grupos[gIndex];
      if (grupo.condicoes.length > 1) {
        grupo.condicoes.splice(cIndex, 1);
        this.emitUpdate();
      }
    },

    emitUpdate() {
      if (this.isVisualMode) {
        this.$emit('input', JSON.stringify(this.conditions));
      } else {
        this.$emit('input', this.rawSql);
      }
      this.debouncePreview();
    },

    onRawSqlChange() {
      this.$emit('input', this.rawSql);
    },

    debouncePreview() {
      if (this.previewTimeout) clearTimeout(this.previewTimeout);
      this.previewTimeout = setTimeout(() => this.refreshPreview(), 800);
    },

    async refreshPreview() {
      if (!this.isVisualMode) {
        this.sqlPreview = '';
        this.previewErro = '';
        return;
      }

      const hasValidConditions = this.conditions.grupos.some(g =>
        g.condicoes.some(c => c.campo && c.operador)
      );

      if (!hasValidConditions) {
        this.sqlPreview = '';
        this.previewErro = '';
        return;
      }

      try {
        const result = await post('/admin/tag-filtro/preview-sql', {
          condicoes: this.conditions,
          entidade: this.entidade,
        });
        this.sqlPreview = result.clausula || '';
        this.previewErro = '';
      } catch (e) {
        // Condição sem valor é erro de cadastro, não silêncio: sem isso a tag
        // seria salva "filtrando" a base inteira.
        this.sqlPreview = '';
        this.previewErro = e && e.message ? e.message : 'Não foi possível gerar o SQL.';
      }
    },
  },

  mounted() {
    if (this.entidade && this.fields.length === 0) {
      this.loadFields(this.entidade);
    }
  },
};
</script>

<style scoped>
/* ============================================================
   BASE
   ============================================================ */
.cb {
  font-size: 13px;
  line-height: 1.5;
  color: #3c4b64;
}

/* ============================================================
   MODE TOGGLE
   ============================================================ */
.cb-mode {
  display: inline-flex;
  border: 1px solid #d8dbe0;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 12px;
}
.cb-mode__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  background: #fff;
  color: #768192;
  cursor: pointer;
  transition: all 0.15s;
}
.cb-mode__btn:hover {
  background: #f0f4f8;
  color: #3c4b64;
}
.cb-mode__btn--active {
  background: #3a3e81;
  color: #fff;
}
.cb-mode__btn--active:hover {
  background: #2e3270;
  color: #fff;
}
.cb-mode__btn + .cb-mode__btn {
  border-left: 1px solid #d8dbe0;
}

/* ============================================================
   VISUAL BUILDER
   ============================================================ */
.cb-visual {
  background: #f4f6f9;
  border: 1px solid #d8dbe0;
  border-radius: 8px;
  padding: 16px;
}

/* Empty / Loading */
.cb-empty,
.cb-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  font-size: 13px;
  color: #768192;
  background: #fff;
  border-radius: 6px;
  border: 1px dashed #c4c9d0;
}
.cb-empty__icon {
  color: #39f;
  flex-shrink: 0;
}

/* ============================================================
   GROUP
   ============================================================ */
.cb-group {
  position: relative;
}

/* Conector AND entre grupos */
.cb-connector {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 12px 0;
}
.cb-connector__line {
  flex: 1;
  height: 1px;
  background: #c4c9d0;
}
.cb-connector__label {
  font-size: 11px;
  font-weight: 700;
  color: #768192;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 10px;
  background: #ebedef;
  border-radius: 10px;
}

.cb-group__card {
  background: #fff;
  border: 1px solid #d8dbe0;
  border-radius: 8px;
  /* overflow visivel para nao clipar o dropdown do multiselect (UF, etc.) */
  overflow: visible;
}

/* Group Header */
.cb-group__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #f8f9fb;
  border-bottom: 1px solid #ebedef;
  /* arredonda os cantos superiores (o card nao usa mais overflow:hidden) */
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}
.cb-group__title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #3c4b64;
}
.cb-group__number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  background: #3a3e81;
  border-radius: 50%;
  flex-shrink: 0;
}
.cb-group__logic {
  appearance: none;
  -webkit-appearance: none;
  padding: 2px 24px 2px 8px;
  font-size: 13px;
  font-weight: 600;
  color: #3a3e81;
  background: #eceaf3 url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='6'%3E%3Cpath d='M0 0l4 6 4-6z' fill='%233a3e81'/%3E%3C/svg%3E") no-repeat right 8px center;
  border: 1px solid #b8b9d0;
  border-radius: 4px;
  cursor: pointer;
  outline: none;
}
.cb-group__logic:focus {
  border-color: #3a3e81;
  box-shadow: 0 0 0 2px rgba(58, 62, 129, 0.15);
}

/* ============================================================
   CONDITIONS
   ============================================================ */
.cb-conditions {
  padding: 12px 14px 4px;
}

.cb-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
}

/* Prefix (Se / E / OU) */
.cb-row__prefix {
  width: 32px;
  flex-shrink: 0;
  padding-top: 7px;
  text-align: right;
}
.cb-row__prefix-text {
  font-size: 13px;
  font-weight: 600;
  color: #768192;
}
.cb-row__prefix-text--logic {
  color: #3a3e81;
}

/* Columns */
.cb-row__field {
  width: 200px;
  flex-shrink: 0;
}
.cb-row__operator {
  width: 150px;
  flex-shrink: 0;
}
.cb-row__value {
  flex: 1;
  min-width: 0;
}
.cb-row__remove {
  flex-shrink: 0;
  padding-top: 4px;
}

/* ============================================================
   FORM CONTROLS (unified)
   ============================================================ */
.cb-select,
.cb-input {
  display: block;
  width: 100%;
  height: 32px;
  padding: 4px 10px;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.5;
  color: #3c4b64;
  background-color: #fff;
  border: 1px solid #d8dbe0;
  border-radius: 4px;
  transition: border-color 0.15s, box-shadow 0.15s;
  outline: none;
}
.cb-select {
  appearance: none;
  -webkit-appearance: none;
  padding-right: 28px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='6'%3E%3Cpath d='M0 0l4 6 4-6z' fill='%23768192'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  cursor: pointer;
}
.cb-select:disabled {
  background-color: #ebedef;
  color: #9da5b1;
  cursor: not-allowed;
}
.cb-select:focus,
.cb-input:focus {
  border-color: #9496b8;
  box-shadow: 0 0 0 2px rgba(58, 62, 129, 0.12);
}
.cb-input::placeholder {
  color: #9da5b1;
}

/* Tipo valor (mini select) */
.cb-select--tipo {
  width: 80px;
  flex-shrink: 0;
  font-size: 12px;
  padding: 4px 22px 4px 6px;
  background-position: right 6px center;
  color: #768192;
}

/* Value select (flex) */
.cb-select--value {
  flex: 1;
  min-width: 0;
}

/* Value group */
.cb-value-group {
  display: flex;
  align-items: flex-start;
  gap: 6px;
}

/* Condição de compra (alvo + período) */
.cb-compra {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.cb-compra__periodo {
  display: flex;
  align-items: center;
  gap: 6px;
}
.cb-select--periodo {
  flex: 1;
  min-width: 0;
}
.cb-input--quantidade {
  width: 72px;
  flex-shrink: 0;
}

/* Between inputs */
.cb-between {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
}
.cb-between__sep {
  font-size: 13px;
  font-weight: 500;
  color: #768192;
  flex-shrink: 0;
}
.cb-between .cb-input,
.cb-between .cb-select {
  flex: 1;
  min-width: 0;
}

/* ============================================================
   BADGES
   ============================================================ */
.cb-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
  white-space: nowrap;
  flex-shrink: 0;
}
.cb-badge--info {
  background: #e8f4fd;
  color: #0d6efd;
}
.cb-badge--muted {
  background: #ebedef;
  color: #768192;
}

/* ============================================================
   ICON BUTTONS
   ============================================================ */
.cb-btn-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 4px;
  background: transparent;
  color: #9da5b1;
  cursor: pointer;
  transition: all 0.15s;
}
.cb-btn-icon:hover {
  color: #3c4b64;
  background: #ebedef;
}
.cb-btn-icon--danger:hover {
  color: #e55353;
  background: #fce4e4;
  border-color: #f5c2c2;
}
.cb-btn-icon:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.cb-btn-icon:disabled:hover {
  background: transparent;
  color: #9da5b1;
}

/* ============================================================
   ADD BUTTONS
   ============================================================ */
.cb-add-condition {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 4px 14px 12px;
  padding: 5px 14px;
  font-size: 13px;
  font-weight: 500;
  color: #3a3e81;
  background: transparent;
  border: 1px dashed #9496b8;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s;
}
.cb-add-condition:hover {
  background: #eceaf3;
  border-color: #3a3e81;
}

.cb-add-group {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  margin-top: 12px;
  padding: 10px;
  font-size: 13px;
  font-weight: 500;
  color: #768192;
  background: #fff;
  border: 1px dashed #c4c9d0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
}
.cb-add-group:hover {
  color: #3a3e81;
  border-color: #9496b8;
  background: #f2f2f8;
}

/* ============================================================
   PREVIEW
   ============================================================ */
.cb-preview {
  margin-top: 14px;
  background: #fff;
  border: 1px solid #d8dbe0;
  border-radius: 8px;
  overflow: hidden;
}
.cb-preview__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 14px;
  background: #f8f9fb;
  border-bottom: 1px solid #ebedef;
}
.cb-preview__title {
  font-size: 12px;
  font-weight: 600;
  color: #768192;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.cb-preview__erro {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 14px;
  font-size: 13px;
  color: #a13c3c;
  background: #fdf1f1;
}
.cb-preview__code {
  margin: 0;
  padding: 14px;
  font-family: 'SFMono-Regular', 'Consolas', 'Liberation Mono', 'Menlo', monospace;
  font-size: 12px;
  line-height: 1.6;
  color: #a6e22e;
  background: #1e1e2e;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 180px;
  overflow-y: auto;
}

/* ============================================================
   SQL EDITOR
   ============================================================ */
.cb-sql {
  border: 1px solid #d8dbe0;
  border-radius: 8px;
  overflow: hidden;
}
</style>

<style>
/* ============================================================
   VUE-MULTISELECT OVERRIDES (unscoped for deep styling)
   ============================================================ */
.cb .cb-multiselect.multiselect {
  min-height: 32px;
  font-size: 13px;
}
.cb .cb-multiselect .multiselect__tags {
  min-height: 32px;
  padding: 4px 40px 0 8px;
  font-size: 13px;
  border: 1px solid #d8dbe0;
  border-radius: 4px;
}
.cb .cb-multiselect .multiselect__tags:focus-within {
  border-color: #9496b8;
  box-shadow: 0 0 0 2px rgba(58, 62, 129, 0.12);
}
.cb .cb-multiselect .multiselect__tag {
  margin-bottom: 2px;
  padding: 2px 22px 2px 8px;
  font-size: 12px;
  background: #3a3e81;
  border-radius: 3px;
}
.cb .cb-multiselect .multiselect__tag-icon::after {
  color: rgba(255, 255, 255, 0.7);
}
.cb .cb-multiselect .multiselect__tag-icon:hover {
  background: #2e3270;
}
.cb .cb-multiselect .multiselect__select {
  height: 32px;
}
.cb .cb-multiselect .multiselect__input,
.cb .cb-multiselect .multiselect__single {
  font-size: 13px;
  margin-bottom: 2px;
  padding: 0;
  line-height: 22px;
}
.cb .cb-multiselect .multiselect__placeholder {
  font-size: 13px;
  color: #9da5b1;
  margin-bottom: 2px;
  padding: 0;
  line-height: 22px;
}
.cb .cb-multiselect .multiselect__option {
  font-size: 13px;
  padding: 8px 12px;
  min-height: 32px;
}
.cb .cb-multiselect .multiselect__option--highlight {
  background: #3a3e81;
}
</style>
